/**
 * GenericRenderer — fallback renderer for unrecognized resource types.
 * Renders structured key-value cards instead of raw JSON dumps.
 * Handles dicts, arrays of dicts, and scalar values gracefully.
 */

import { useState } from 'react'
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'
import { SupplementSections } from './SupplementSections'
import { MessageContent } from '../MessageContent'

// Fields never shown — internal IDs, API plumbing, template URLs
const NOISE_FIELDS = new Set([
  'node_id', 'permissions', 'topics', 'owner',
  'trees_url', 'statuses_url', 'subscribers_url', 'subscription_url',
  'commits_url', 'git_commits_url', 'git_tags_url', 'git_refs_url',
  'blobs_url', 'tags_url', 'branches_url', 'collaborators_url',
  'comments_url', 'compare_url', 'contents_url', 'contributors_url',
  'deployments_url', 'downloads_url', 'events_url', 'forks_url',
  'git_url', 'hooks_url', 'issue_comment_url', 'issue_events_url',
  'issues_url', 'keys_url', 'labels_url', 'languages_url',
  'merges_url', 'milestones_url', 'notifications_url', 'pulls_url',
  'releases_url', 'stargazers_url', 'archive_url', 'assignees_url',
  'ssh_url', 'svn_url', 'clone_url', 'mirror_url',
])

// Fields used as the primary label — shown as the card heading, not repeated
const LABEL_FIELDS = [
  'full_name', 'name', 'key', 'title', 'login',
  'display_name', 'slug', 'filename', 'path', 'label',
]

// URL fields worth showing (user-facing links, not API endpoints)
const VISIBLE_URL_FIELDS = new Set([
  'html_url', 'web_url', 'homepage', 'url', 'browse_url',
])

function isNoiseField(key: string): boolean {
  if (NOISE_FIELDS.has(key)) return true
  // Any _url field except the visible ones
  if (key.endsWith('_url') && !VISIBLE_URL_FIELDS.has(key)) return true
  return false
}

function prettyKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function pickLabel(item: Record<string, unknown>): string {
  for (const field of LABEL_FIELDS) {
    const val = item[field]
    if (val && typeof val === 'string') return val
  }
  return ''
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '–'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return val > 9999 ? val.toLocaleString() : String(val)
  if (typeof val === 'string') {
    if (val.length > 200) return val.slice(0, 197) + '...'
    return val
  }
  if (Array.isArray(val)) return `${val.length} items`
  if (typeof val === 'object') {
    const inner = val as Record<string, unknown>
    const innerLabel = pickLabel(inner)
    if (innerLabel) return innerLabel
    const keys = Object.keys(inner).slice(0, 3)
    return keys.map(k => `${prettyKey(k)}: ${formatValue(inner[k])}`).join(', ')
  }
  return String(val)
}

// ---------------------------------------------------------------------------
// Single record card — renders one dict as a clean key-value card
// ---------------------------------------------------------------------------

function RecordCard({ data, index }: { data: Record<string, unknown>; index?: number }) {
  const label = pickLabel(data)

  // Collect visible fields (skip noise and the label field itself)
  const fields = Object.entries(data).filter(([k, v]) => {
    if (isNoiseField(k)) return false
    if (k.startsWith('_')) return false
    if (v === null || v === undefined || v === '') return false
    // Skip the field used as label
    if (label && typeof v === 'string' && v === label && LABEL_FIELDS.includes(k)) return false
    return true
  })

  return (
    <div className="rounded-lg border border-subtle bg-foreground/[0.03] p-3">
      {/* Label header */}
      {label && (
        <div className="flex items-center gap-2 mb-2">
          {index !== undefined && (
            <span className="shrink-0 w-5 h-5 rounded-full bg-foreground/[0.06] text-[10px] font-semibold text-muted-foreground flex items-center justify-center">
              {index + 1}
            </span>
          )}
          <span className="text-xs font-semibold text-foreground truncate">{label}</span>
        </div>
      )}

      {/* Fields */}
      <div className="space-y-1">
        {fields.map(([key, val]) => (
          <div key={key} className="flex items-start gap-2 text-xs">
            <span className="shrink-0 min-w-[90px] text-subtle-foreground font-medium">{prettyKey(key)}</span>
            {typeof val === 'string' && (val.startsWith('http://') || val.startsWith('https://')) ? (
              <a
                href={val}
                target="_blank"
                rel="noopener noreferrer"
                className="text-info-foreground hover:underline truncate"
              >
                {val}
              </a>
            ) : typeof val === 'object' && val !== null && !Array.isArray(val) ? (
              <NestedObject data={val as Record<string, unknown>} />
            ) : (
              <span className="text-foreground break-words">{formatValue(val)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Nested object — collapsible inline display
// ---------------------------------------------------------------------------

function NestedObject({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false)
  const label = pickLabel(data)
  const preview = label || formatValue(data)

  const fields = Object.entries(data).filter(([k, v]) => {
    if (isNoiseField(k)) return false
    if (v === null || v === undefined || v === '') return false
    return true
  })

  if (fields.length <= 2) {
    // Small objects: show inline
    return (
      <span className="text-foreground">
        {fields.map(([k, v], i) => (
          <span key={k}>
            {i > 0 && <span className="text-subtle-foreground mx-1">·</span>}
            <span className="text-subtle-foreground">{prettyKey(k)}:</span>{' '}
            <span>{formatValue(v)}</span>
          </span>
        ))}
      </span>
    )
  }

  return (
    <div className="flex-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition"
      >
        {open
          ? <ChevronDown className="w-3 h-3 shrink-0" />
          : <ChevronRight className="w-3 h-3 shrink-0" />
        }
        <span className="truncate">{preview}</span>
      </button>
      {open && (
        <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-subtle pl-2">
          {fields.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2 text-xs">
              <span className="shrink-0 text-subtle-foreground font-medium">{prettyKey(k)}</span>
              <span className="text-foreground break-words">{formatValue(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// GenericRenderer
// ---------------------------------------------------------------------------

export function GenericRenderer({ data }: { data: unknown }) {
  const [copied, setCopied] = useState(false)

  const isObject = data && typeof data === 'object' && !Array.isArray(data)
  const record = isObject ? data as Record<string, unknown> : null

  // Separate primary fields from supplement fields (prefixed with _)
  const primaryEntries = record
    ? Object.entries(record).filter(([k]) => !k.startsWith('_'))
    : []

  const hasSupplements = record
    ? Object.keys(record).some(k => k.startsWith('_'))
    : false

  const hasPrimaryData = Array.isArray(data)
    ? (data as unknown[]).length > 0
    : primaryEntries.length > 0

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // File content shortcut — render directly
  const fileContent = record?._fileContent
  if (!hasPrimaryData && typeof fileContent === 'string') {
    return (
      <div className="text-[10px] text-foreground leading-relaxed">
        <MessageContent content={fileContent} />
      </div>
    )
  }

  // --- Array of items ---
  if (Array.isArray(data)) {
    const items = data as unknown[]
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{items.length} item{items.length !== 1 ? 's' : ''}</span>
          <CopyButton copied={copied} onCopy={handleCopy} />
        </div>
        <div className="space-y-2">
          {items.map((item, i) =>
            item && typeof item === 'object' && !Array.isArray(item)
              ? <RecordCard key={i} data={item as Record<string, unknown>} index={i} />
              : (
                <div key={i} className="rounded-lg border border-subtle bg-foreground/[0.03] px-3 py-2 text-xs text-foreground">
                  {formatValue(item)}
                </div>
              )
          )}
        </div>
      </div>
    )
  }

  // --- Single object ---
  if (record && hasPrimaryData) {
    const primaryRecord = Object.fromEntries(primaryEntries) as Record<string, unknown>
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-end">
          <CopyButton copied={copied} onCopy={handleCopy} />
        </div>
        <RecordCard data={primaryRecord} />
        {hasSupplements && <SupplementSections data={record} />}
      </div>
    )
  }

  // --- Scalar or empty ---
  if (!hasPrimaryData && hasSupplements && record) {
    return <SupplementSections data={record} />
  }

  return (
    <div className="text-xs text-muted-foreground text-center py-6">No data available</div>
  )
}

// ---------------------------------------------------------------------------
// Shared copy button
// ---------------------------------------------------------------------------

function CopyButton({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-1 text-xs text-subtle-foreground hover:text-muted-foreground transition px-2 py-1 rounded hover:bg-foreground/[0.06]"
      title="Copy raw JSON"
    >
      {copied ? <Check className="w-3 h-3 text-success-foreground" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}
